@if (isOpen) {
  <div class="nt-scrim" (click)="close()" aria-hidden="true"></div>

  <aside
    #panel
    class="nt-panel"
    role="dialog"
    aria-modal="true"
    aria-labelledby="nt-title"
    tabindex="-1"
    (keydown)="onKeydown($event)"
    cdkTrapFocus
  >
    <div class="nt-h">
      <h2 id="nt-title">Alerts</h2>
      <!--
        The count only exists when there is something to count. A green "0"
        beside the title read as a badge that had been cleared, and on a failed
        read it read as a confirmed none, which is exactly the claim this
        drawer cannot make.
      -->
      @if (state() === 'rows') {
        <span class="nt-n">{{ notifications().length }}</span>
      }
      <button
        class="nt-close"
        type="button"
        aria-label="Close alerts"
        (click)="close()"
      >
        <fa-icon [icon]="['fal', 'times']"></fa-icon>
      </button>
    </div>

    <div class="nt-list">
      <!--
        Three states, and they have to look like three states. All of them used
        the same quiet grey paragraph, so "loading", "we could not read your
        alerts" and "you have none" were the same sentence in the same place
        and a member had to read the words to tell a failure from a clear
        drawer. Each one now carries its own lead, and the failure carries a
        retry, which is the only one of the three there is anything to do about.
      -->
      @if (state() === 'loading') {
        <p class="nt-empty" role="status">Loading your alerts...</p>
      } @else if (state() === 'failed') {
        <!-- Not the same as having none: a member with an unread counselor
             reply must not be told there is nothing waiting. -->
        <div class="nt-empty nt-empty-failed" role="alert">
          <p class="nt-empty-h">Your alerts did not load</p>
          <p>
            This is a problem reading them, not a confirmed empty list. Anything
            waiting for you is still there.
          </p>
          <button class="nt-retry" type="button" (click)="retry()">
            Try again
          </button>
        </div>
      } @else if (state() === 'empty') {
        <!--
          Truthful about the whole feature, not just about today.
          `api.notification` is read and dismissed and nothing in the product,
          in any resolver, cron job or AI service, inserts a row, so this is
          the state every member is in and will stay in until a producer is
          built. Saying "replies, responses and results turn up here" promised
          traffic that cannot arrive; saying nothing has come through yet and
          naming what would is the honest version, and it does not read as a
          failure or as a load that never finished.
        -->
        <div class="nt-empty">
          <p class="nt-empty-h">No alerts yet</p>
          <p>
            Nothing has come through for you. When it does, this is where it
            lands: a reply from your counselor, a response from a school you
            asked about, and results that finish while you are away.
          </p>
          <p class="nt-empty-note">
            Nothing is hidden behind this panel. An empty drawer means an empty
            drawer.
          </p>
        </div>
      } @else {
        @if (waiting().length) {
          <p class="nt-grp" id="nt-grp-waiting">Waiting on you</p>
          <ul class="nt-rows" aria-labelledby="nt-grp-waiting">
            @for (row of waiting(); track row.id) {
              <ng-container
                *ngTemplateOutlet="notificationRow; context: { $implicit: row }"
              ></ng-container>
            }
          </ul>
        }

        @if (informational().length) {
          <p class="nt-grp" id="nt-grp-info">For information</p>
          <ul class="nt-rows" aria-labelledby="nt-grp-info">
            @for (row of informational(); track row.id) {
              <ng-container
                *ngTemplateOutlet="notificationRow; context: { $implicit: row }"
              ></ng-container>
            }
          </ul>
        }
      }
    </div>
  </aside>
}

<ng-template #notificationRow let-row>
  <li class="nt-row" [attr.data-kind]="row.kind.toLowerCase()">
    <span class="nt-ic" aria-hidden="true">
      <fa-icon [icon]="['far', iconFor(row.kind)]"></fa-icon>
    </span>

    <span class="nt-m">
      <b>{{ row.title }}</b>
      @if (row.body) {
        <span>{{ row.body }}</span>
      }
    </span>

    <!-- The exact time stays reachable on the title, because a relative one is
         what the drawer needs and an absolute one is what a record needs. -->
    <span class="nt-when" [title]="row.createdAt | date: 'medium'">
      {{ relativeTime(row.createdAt) }}
    </span>

    <!-- Label and path are both present or both absent by construction, so
         there is no row with a button that goes nowhere. -->
    @if (row.destinationPath) {
      <button class="nt-go" type="button" (click)="goTo(row)">
        {{ row.destinationLabel }}
      </button>
    }

    <button
      class="nt-x"
      type="button"
      [attr.aria-label]="'Dismiss: ' + row.title"
      (click)="dismiss(row)"
    >
      <fa-icon [icon]="['fal', 'times']"></fa-icon>
    </button>
  </li>
</ng-template>
